import {
  BellOff,
  BellRing,
  LoaderCircle,
} from "lucide-react";
import { useEffect, useRef, useState } from "react";

import {
  deletePushSubscription,
  getPushConfig,
  savePushSubscription,
} from "../api";
import {
  isIos,
  isStandalone,
  supportsWebPush,
  urlBase64ToUint8Array,
} from "../pwa";
import type {
  PushConfig,
  PushSubscriptionPayload,
} from "../types";


interface Props {
  token: string;
}

function deviceLabel() {
  if (isIos()) return "iPhone o iPad";
  if (/Android/i.test(navigator.userAgent)) return "Android";
  if (/Macintosh|Mac OS X/i.test(navigator.userAgent)) return "Mac";
  if (/Windows/i.test(navigator.userAgent)) return "Windows";
  return "Browser";
}

function subscriptionPayload(
  subscription: PushSubscription,
): PushSubscriptionPayload {
  const serialized = subscription.toJSON();
  const keys = serialized.keys;
  if (!serialized.endpoint || !keys?.p256dh || !keys.auth) {
    throw new Error("Il browser non ha restituito una sottoscrizione valida.");
  }
  return {
    endpoint: serialized.endpoint,
    expiration_time: serialized.expirationTime ?? null,
    keys: {
      p256dh: keys.p256dh,
      auth: keys.auth,
    },
    device_label: deviceLabel(),
  };
}

export default function NotificationBell({ token }: Props) {
  const [pushConfig, setPushConfig] = useState<PushConfig | null>(null);
  const [subscribed, setSubscribed] = useState(false);
  const [permission, setPermission] = useState<NotificationPermission>(
    supportsWebPush() ? Notification.permission : "denied",
  );
  const [busy, setBusy] = useState(false);
  const [message, setMessage] = useState("");
  const [open, setOpen] = useState(false);
  const rootRef = useRef<HTMLDivElement>(null);

  useEffect(() => {
    if (!supportsWebPush()) return;
    let active = true;

    async function loadState() {
      try {
        const config = await getPushConfig(token);
        if (!active) return;
        setPushConfig(config);
        setPermission(Notification.permission);
        if (!config.enabled || Notification.permission !== "granted") return;

        const registration = await navigator.serviceWorker.ready;
        const existing = await registration.pushManager.getSubscription();
        if (!active || !existing) return;
        await savePushSubscription(token, subscriptionPayload(existing));
        if (active) setSubscribed(true);
      } catch {
        if (active) {
          setMessage("Impossibile verificare lo stato delle notifiche.");
        }
      }
    }

    void loadState();
    return () => {
      active = false;
    };
  }, [token]);

  useEffect(() => {
    if (!open) return;
    const onPointerDown = (event: PointerEvent) => {
      if (!rootRef.current?.contains(event.target as Node)) setOpen(false);
    };
    const onKeyDown = (event: KeyboardEvent) => {
      if (event.key === "Escape") setOpen(false);
    };
    document.addEventListener("pointerdown", onPointerDown);
    document.addEventListener("keydown", onKeyDown);
    return () => {
      document.removeEventListener("pointerdown", onPointerDown);
      document.removeEventListener("keydown", onKeyDown);
    };
  }, [open]);

  async function enableNotifications() {
    setMessage("");
    if (!supportsWebPush()) {
      setMessage("Questo browser non supporta le notifiche Web Push.");
      return;
    }
    if (isIos() && !isStandalone()) {
      setMessage(
        "Prima installa l’app dalla schermata Condividi, poi riaprila dalla Home.",
      );
      return;
    }
    if (!pushConfig?.enabled || !pushConfig.public_key) {
      setMessage("Il server delle notifiche non è ancora configurato.");
      return;
    }

    setBusy(true);
    let createdSubscription: PushSubscription | null = null;
    try {
      let nextPermission = Notification.permission;
      if (nextPermission === "default") {
        nextPermission = await Notification.requestPermission();
      }
      setPermission(nextPermission);
      if (nextPermission !== "granted") {
        setMessage(
          nextPermission === "denied"
            ? "Notifiche bloccate: riattivale dalle impostazioni del browser."
            : "Autorizzazione alle notifiche non concessa.",
        );
        return;
      }

      const registration = await navigator.serviceWorker.ready;
      let subscription = await registration.pushManager.getSubscription();
      if (!subscription) {
        subscription = await registration.pushManager.subscribe({
          userVisibleOnly: true,
          applicationServerKey: urlBase64ToUint8Array(pushConfig.public_key),
        });
        createdSubscription = subscription;
      }
      await savePushSubscription(token, subscriptionPayload(subscription));
      setSubscribed(true);
      setMessage("Notifiche attive su questo dispositivo.");
    } catch (caught) {
      if (createdSubscription) {
        await createdSubscription.unsubscribe().catch(() => false);
      }
      setMessage(
        caught instanceof Error
          ? caught.message
          : "Non è stato possibile attivare le notifiche.",
      );
    } finally {
      setBusy(false);
    }
  }

  async function disableNotifications() {
    setBusy(true);
    setMessage("");
    try {
      const registration = await navigator.serviceWorker.ready;
      const subscription = await registration.pushManager.getSubscription();
      if (subscription) {
        try {
          await deletePushSubscription(token, subscription.endpoint);
        } finally {
          await subscription.unsubscribe();
        }
      }
      const badgeNavigator = navigator as Navigator & {
        clearAppBadge?: () => Promise<void>;
      };
      if (badgeNavigator.clearAppBadge) {
        await badgeNavigator.clearAppBadge();
      }
      setSubscribed(false);
      setMessage("Notifiche disattivate su questo dispositivo.");
    } catch {
      setMessage("Non è stato possibile disattivare completamente le notifiche.");
    } finally {
      setBusy(false);
    }
  }

  const label = subscribed
    ? "Notifiche attive"
    : permission === "denied"
      ? "Notifiche bloccate"
      : "Notifiche disattivate";

  return (
    <div className="notification-bell" ref={rootRef}>
      <button
        className={`icon-button bell-button ${subscribed ? "active" : ""}`}
        onClick={() => {
          setMessage("");
          setOpen((value) => !value);
        }}
        title={label}
        aria-label={label}
        aria-expanded={open}
        aria-haspopup="dialog"
      >
        {subscribed ? <BellRing size={17} /> : <BellOff size={17} />}
      </button>
      {open && (
        <div className="notification-popover" role="dialog" aria-label="Notifiche">
          <strong>{label}</strong>
          <p>
            {subscribed
              ? "Vuoi disattivare le notifiche su questo dispositivo?"
              : permission === "denied"
                ? "Le notifiche sono bloccate: riattivale dalle impostazioni del browser."
                : "Vuoi attivare le notifiche su questo dispositivo?"}
          </p>
          {message && <p className="notification-popover-message" role="status">{message}</p>}
          <div className="notification-popover-actions">
            <button className="popover-button ghost" onClick={() => setOpen(false)}>
              Annulla
            </button>
            {permission !== "denied" && (
              <button
                className="popover-button primary"
                onClick={() =>
                  void (subscribed ? disableNotifications() : enableNotifications())
                }
                disabled={busy}
              >
                {busy ? (
                  <LoaderCircle size={14} className="spin" />
                ) : subscribed ? (
                  "Disattiva"
                ) : (
                  "Attiva"
                )}
              </button>
            )}
          </div>
        </div>
      )}
    </div>
  );
}
